// ─── "Days from launch" — the number, and the filter clip it sets ─────────────
// Pure, no React. Mirrored on the server (server/launchDays.js) because the
// server can't synchronously import a browser module; test/launchDays.test.js
// runs BOTH over the same cases and fails if the copies drift.
//
// Sibling of the days-to-go sync (ViewPage.applyDaysToGo / query.daysOverlayFor).
// The only real difference is where the number comes from:
//
//   • days-to-go     reads a live days-before-event value off a source TILE;
//   • days-from-launch is today minus the launch date.
//
// Which is why this one needs no scheduler and no Looker round-trip: the answer
// is recomputed from the calendar every time the dashboard is rendered, so the
// upper bracket advances by one at every day boundary on its own. That was the
// whole ask — the bracket on a "Days from Launch" filter used to be typed by
// hand and went stale the next morning, so reports quietly stopped including
// the most recent days until someone remembered to widen it.

export const DEFAULT_LAUNCH_EXPR = '<={n}';
// Day boundaries follow the reporting timezone, not the viewer's — a dashboard
// opened from London must count the same day as the one opened in Cape Town, or
// the same report shows two different brackets. (server/launchDays.js reads
// REPORTING_TIMEZONE; the browser has no env, so it uses this default.)
export const REPORTING_TZ = 'Africa/Johannesburg';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// Today's calendar date in `tz`, as YYYY-MM-DD (en-CA renders ISO order).
export function todayISO(tz = REPORTING_TZ, now = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  } catch {
    return new Date(now).toISOString().slice(0, 10); // unknown tz → UTC rather than nothing
  }
}

// Whole calendar days from `anchor` (YYYY-MM-DD) to today. Launch day itself is
// 0, so the number reads straight onto a days-from-launch axis. Negative before
// launch. null when there's no usable anchor.
export function daysSince(anchor, { tz = REPORTING_TZ, now = new Date() } = {}) {
  if (!ISO_DATE.test(String(anchor || ''))) return null;
  const from = Date.parse(`${anchor}T00:00:00Z`);
  const to = Date.parse(`${todayISO(tz, now)}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.round((to - from) / 86400000);
}

// The launch date OUT OF a filter value. Looker writes its own date filter
// expressions with SLASHES ("2026/08/01"), often as "on 2026/08/01", a midnight
// timestamp, or a one-day range ("2026/08/01 to 2026/08/02") — and an imported
// dashboard arrives carrying exactly those. Accepting only a bare "2026-08-01"
// meant every real launch-date filter was silently unreadable, so the sync did
// nothing and the bracket sat on its default forever (ticket #168). Same lesson
// as client/src/lib/dateFilter.mjs. "before X" names no launch day, so it's out.
// → 'YYYY-MM-DD' | ''
export function launchDateOf(value) {
  const s = String(value == null ? '' : value).trim();
  if (!s || /^before\b/i.test(s)) return '';
  const m = s.match(/^(?:(?:on|after|on or after|from)\s+)?(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?=$|[\sT])/i);
  if (!m) return '';
  const iso = `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  return Number.isNaN(Date.parse(`${iso}T00:00:00Z`)) ? '' : iso;
}

// The whole rule in one call.
//   sync       — def.daysFromLaunchSync: { mode, sourceFilterName, filterName, expr }
//   anchorDate — the launch date to count from (YYYY-MM-DD), already resolved by
//                the caller from the dashboard filter sync.sourceFilterName names
//                (e.g. "Current Event Days From Launch Date") — this function
//                doesn't care where it came from
// → { raw, n, anchor, mode, overlay } | null   (overlay = { [filterName]: expr })
export function resolveLaunchDays(sync, anchorDate, opts = {}) {
  const s = sync || {};
  if (!s.mode || s.mode === 'off') return null;
  const anchor = launchDateOf(anchorDate);
  const raw = daysSince(anchor, opts);
  if (raw == null) return null;
  const n = Math.max(0, raw); // there is no "day −3" on a days-from-launch axis
  // BEFORE launch we show the countdown but set no clip: "<=0" on a not-yet-open
  // campaign would blank every tile, which is worse than the stale bracket this
  // feature exists to fix.
  const overlay = (s.mode === 'apply' && s.filterName && raw >= 0)
    ? { [s.filterName]: String(s.expr || DEFAULT_LAUNCH_EXPR).replace('{n}', String(n)) }
    : null;
  return { raw, n, anchor, mode: s.mode, overlay };
}

// Natural-language label for the header chip, sign-aware.
export function launchDaysLabel(raw) {
  if (raw === 0) return 'Launch day';
  if (raw > 0) return `Day ${raw} from launch`;
  const a = Math.abs(raw);
  return `Launches in ${a} ${a === 1 ? 'day' : 'days'}`;
}
